/**
 * AI-forbruk fordelt på funksjon.
 *
 * Finnes fordi GCP-fakturaen viser «Vertex AI» som ÉN linje uten fordeling, og
 * GCP har ikke noe API for sanntidsforbruk. Vertex returnerer derimot eksakte
 * tokentall på hvert svar, så vi måler selv.
 *
 * Kostnaden er et ESTIMAT — enhetsprisene er hardkodet og Google endrer dem.
 * Fasit er alltid Cloud Billing. Verdien her er fordelingen: hvilken funksjon
 * bruker tokenene.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Cpu, AlertTriangle, ChevronDown } from 'lucide-react';
import { getAiUsage, type AiUsage } from '../../../services/articleService';
import { Button } from '../../ui/Button';

const FEATURE_LABEL: Record<string, string> = {
  'artikkel-generering': 'Artikkelgenerering',
  'artikkel-oversettelse': 'Oversettelse',
  'artikkel-bilde': 'Bildegenerering',
  'artikkel-bildeprompt': 'Bildeprompt',
  ukjent: 'Umerket',
};

const fmt = (n: number) => n.toLocaleString('nb-NO');
/** Kroner er hovedtallet — dollar er implementasjonsdetalj fra Googles prisliste. */
const nok = (n: number) => `${n < 0.01 && n > 0 ? n.toFixed(3) : n.toFixed(2)} kr`;

const AiUsagePanel: React.FC = () => {
  const [data, setData] = useState<AiUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  // Kollapset som default: forbruket er noe du sjekker, ikke noe du jobber i.
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = React.useCallback(async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      setData(await getAiUsage(d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke hente forbruk');
    } finally {
      setLoading(false);
    }
  }, []);

  // Hent først når panelet åpnes — et lukket panel skal ikke koste et kall.
  useEffect(() => { if (open) load(days); }, [load, days, open]);

  const max = Math.max(...(data?.features || []).map((f) => f.usd), 0.0001);

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-panel p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-3 text-emerald-400 text-left rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
        >
          <Cpu size={20} className="flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-[var(--text-primary)]">Forbruk</h3>
            <p className="text-xs text-[var(--text-muted)]">
              {open ? `Siste ${days} dager, fordelt på funksjon` : 'Prosjektkostnad og AI-bruk'}
            </p>
          </div>
          <ChevronDown size={16} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
        <div className="flex items-center gap-1.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              aria-pressed={days === d}
              className={`px-3 py-1.5 rounded-pill border text-tiny font-black uppercase tracking-widest transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ${
                days === d
                  ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                  : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-muted)]'
              }`}
            >
              {d} d
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => load(days)} disabled={loading} aria-label="Oppdater">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        )}
      </div>

      {!open && (
        <p className="text-xs text-[var(--text-muted)] leading-relaxed">
          Faktisk kostnad for hele Firebase-prosjektet, og hvor AI-tokenene går.
          Utvid for å se.
        </p>
      )}

      {open && error && (
        <p className="text-xs font-bold text-red-400 bg-red-500/10 rounded-card px-3 py-2">{error}</p>
      )}

      {open && (loading && !data ? (
        <div className="flex items-center justify-center py-10 text-[var(--text-muted)]">
          <Loader2 size={18} className="animate-spin" />
        </div>
      ) : !data || data.features.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] py-8 text-center">
          Ingen AI-kall registrert ennå. Målingen startet ved siste deploy og teller
          bare framover — ingen historikk bakover.
        </p>
      ) : (
        <>
          {/* Faktisk forbruk for HELE prosjektet — pushet fra Cloud Billing-budsjettet.
              Står øverst fordi det er ekte kroner; token-estimatet under forklarer
              hvor AI-delen av dem går. */}
          {data.gcp && (
            <div className="rounded-card bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-tiny font-black uppercase tracking-widest text-[var(--text-muted)]">
                  Firebase-prosjektet · faktisk
                </span>
                <span className="text-xs tabular-nums text-[var(--text-muted)]">
                  av {data.gcp.budget.toLocaleString('nb-NO')} {data.gcp.currency}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold tabular-nums text-[var(--text-primary)]">
                  {data.gcp.cost.toLocaleString('nb-NO', { maximumFractionDigits: 2 })} {data.gcp.currency}
                </span>
              </div>
              <div className="h-1.5 rounded-pill bg-[var(--bg-surface)] overflow-hidden">
                <div
                  className={`h-full rounded-pill ${
                    data.gcp.cost / Math.max(data.gcp.budget, 1) > 0.9 ? 'bg-red-400' : 'bg-emerald-400/70'
                  }`}
                  style={{ width: `${Math.min((data.gcp.cost / Math.max(data.gcp.budget, 1)) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold tabular-nums text-[var(--text-primary)]">
              {nok(data.totalNok)}
            </span>
            <span className="text-xs text-[var(--text-muted)]">
              AI-estimat, {days} dager · ${data.totalUsd.toFixed(2)}
            </span>
          </div>

          <div className="space-y-2">
            {data.features.map((f) => (
              <div key={f.feature} className="space-y-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-xs font-bold text-[var(--text-secondary)] truncate">
                    {FEATURE_LABEL[f.feature] || f.feature}
                  </span>
                  <span className="text-xs tabular-nums text-[var(--text-muted)] flex-shrink-0">
                    {fmt(f.calls)} kall · {nok(f.nok)}
                  </span>
                </div>
                <div className="h-1.5 rounded-pill bg-[var(--bg-surface-alt)] overflow-hidden">
                  <div
                    className="h-full bg-emerald-400/70 rounded-pill"
                    style={{ width: `${Math.max((f.usd / max) * 100, 2)}%` }}
                  />
                </div>
                {f.thoughtTokens > 0 && (
                  <p className="text-tiny text-[var(--text-muted)] tabular-nums">
                    {fmt(f.promptTokens)} inn · {fmt(f.outputTokens)} ut ·{' '}
                    <span className="text-amber-400/80">{fmt(f.thoughtTokens)} tenking</span>
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-start gap-2 pt-1 text-[var(--text-muted)]">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-amber-400/70" />
            <p className="text-tiny leading-relaxed">
              Toppbeløpet er faktisk kostnad for HELE Firebase-prosjektet —
              Firestore, Functions, lagring, hosting og AI samlet — pushet fra
              Cloud Billing. AI-tallene under er et estimat med hardkodede
              enhetspriser og fast kurs {data.usdToNok} kr/$, og finnes fordi
              fakturaen viser «Vertex AI» som én linje uten fordeling.
              Tenketokens faktureres som output og er tatt med.
            </p>
          </div>
        </>
      ))}
    </div>
  );
};

export default AiUsagePanel;
